import React, { useEffect, useState, useRef } from 'react';
import Alert from '@jbhi-fi/lanyard-ui/components/Alert';
import Grid from '@jbhi-fi/lanyard-ui/components/Grid';
import { StoreDetails, StoreHoursData } from './types';
import { AlgoliaApiClient } from '../../../api/localisation/AlgoliaApiClient';
import StoreMap from './StoreMap';
import { styles } from './StoreLayout.css';
import { Link } from '@jbhi-fi/components/src/PlatformFunctionality';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { formatStoreAddress, formatStoreHours } from './utils/storeLayoutUtils';
import LocationIcon from '../../../../assets/svg/icon-store-finder.svg';
import { ArrowLeft, Phone, StoreIllustrated } from '@jbhi-fi/lanyard-ui/components/Icons';
import Button from '@jbhi-fi/lanyard-ui/components/Button';

type LinkButtonProps = {
  label: string;
  icon: JSX.Element;
  isLink?: boolean;
  address?: Array<string | boolean>;
};

const LinkButton: React.FC<LinkButtonProps> = ({ label, icon, address = null, isLink = false }) => {
  let targetLink = '#';
  let target = '_self';
  let datatestid = 'store-phone';
  if (isLink) {
    const encodedAddress = address ? encodeURIComponent(address.join(' ').toString()) : '';
    targetLink = `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;
    target = '_blank';
    datatestid = 'store-directions';
  } else {
    targetLink = `tel:${label}`;
  }

  return (
    <a href={targetLink} target={target} rel={isLink ? 'noreferrer' : undefined}>
      <Button
        type="linkWithIcon"
        label={label}
        onClick={() => {}}
        icon={icon}
        iconPosition="left"
        data-testid={datatestid}
      />
    </a>
  );
};

type StoreLayoutProps = StoreDetails & {
  isPopOut?: boolean;
  onBackClick?: () => void;
};

type StoreMessageType = 'info' | 'warning';

type StoreDataType = StoreDetails & {
  productRange: Array<string>;
  locationType?: 'Home' | 'Store';
  _geoloc: {
    lat: number;
    lng: number;
  };
  message?: string;
  messageType?: StoreMessageType;
  showSalesLineCallout: boolean;
};

const defaultStoreData = {
  shopId: '',
  pageTitle: '',
  storeLocationType: '',
  storePhone: '',
  storeAddress: {},
  storeHours: [],
  productRange: [],
  locationType: undefined,
  _geoloc: {
    lat: 0,
    lng: 0,
  },
  message: '',
  messageType: 'info' as StoreMessageType,
  showSalesLineCallout: false,
};

const StoreLayout = ({ shopId, isPopOut = false, onBackClick }: StoreLayoutProps) => {
  const isDesktop = useBreakpoint('md', 'up');

  const [storeData, setStoreData] = useState<StoreDataType & StoreDetails>(defaultStoreData);
  const [loading, setLoading] = useState(true);

  const focusReference = useRef(null);
  const jobVacanciesUrl = window.Jbhifi?.jobVacanciesUrl;
  const jbSalesLine = window.Jbhifi?.jbSalesLine;
  const mapType = window.Jbhifi?.storePageSettings?.[isDesktop ? 'mapTypeDesktop' : 'mapTypeMobile'] ?? 'default';

  useEffect(() => {
    (focusReference.current as HTMLElement | null)?.focus();
  }, []);

  useEffect(() => {
    if (!shopId) return;
    const asyncStoreData = async () => {
      setLoading(true);
      const algoliaStoreData = await AlgoliaApiClient.getStore(shopId);
      if (!algoliaStoreData) {
        setLoading(false);
        return;
      }
      const locationType = algoliaStoreData?.locationType ?? defaultStoreData.locationType;
      const _geoloc = algoliaStoreData?._geoloc ?? defaultStoreData._geoloc;
      const message = algoliaStoreData?.message || undefined;

      let messageType: StoreMessageType | undefined = undefined;
      switch (algoliaStoreData?.messageType) {
        case 'Information':
          messageType = 'info';
          break;
        case 'Warning':
          messageType = 'warning';
          break;
      }

      let productRange = defaultStoreData.productRange;
      if (
        algoliaStoreData?.categoriesRanged?.length &&
        algoliaStoreData.categoriesRanged[0].LevelTwoCategories.length
      ) {
        productRange = algoliaStoreData.categoriesRanged[0].LevelTwoCategories;
      }

      setStoreData({
        shopId,
        pageTitle: algoliaStoreData?.storeName,
        storeLocationType: algoliaStoreData?.locationType ?? '',
        storePhone: algoliaStoreData?.phone,
        storeAddress: formatStoreAddress(algoliaStoreData?.storeAddress),
        storeHours: formatStoreHours(algoliaStoreData?.scheduledTradingHoursForTheComingWeek),
        productRange,
        locationType,
        _geoloc,
        message,
        messageType,
        showSalesLineCallout: algoliaStoreData?.showSalesLineCallout || false,
      });

      setLoading(false);
    };
    asyncStoreData();
  }, [shopId]);

  const handleBackClick = () => {
    if (isPopOut && onBackClick) {
      onBackClick();
    } else {
      window.location.href = '/pages/store-finder';
    }
  };

  const variant = isPopOut ? 'isPopOut' : 'default';
  const selectedStoreAddress = storeData.storeAddress;
  const storeAddressParts = [
    selectedStoreAddress?.AddressLine1 ?? false,
    selectedStoreAddress?.AddressLine2 ?? false,
    selectedStoreAddress?.AddressLine3 ?? false,
    [selectedStoreAddress?.Suburb, selectedStoreAddress?.State?.toUpperCase(), selectedStoreAddress?.Postcode]
      .filter(Boolean)
      .join(', '),
  ].filter(Boolean);
  const getOpeningHours = () => {
    return storeData?.storeHours?.map((day: StoreHoursData) => {
      const variant = day.DayOfWeek.includes('Open');
      return (
        <tr key={day.DayOfWeek} className={styles.detailsHoursTableRow({ variant })}>
          <td className={styles.detailsHoursTableCell}>{day.DayOfWeek}</td>
          <td className={styles.detailsHoursTableCell}>{day.Date}</td>
          <td className={styles.detailsHoursTableCell}>{day.Hours}</td>
        </tr>
      );
    });
  };

  const getIconDimensions = () => {
    return isPopOut || !isDesktop ? { width: '48px', height: '31px' } : { width: '84.33px', height: '50px' };
  };
  if (loading) {
    return null;
  }

  return (
    <div id="store-finder">
      <div className={styles.pageHeader({ variant })}>
        <Button
          onClick={handleBackClick}
          data-testid="back-to-stores-link"
          type="linkWithIcon"
          iconPosition="left"
          tabIndex={-1}
          label={`Back ${!isPopOut ? ' to all stores' : ''}`}
          icon={<ArrowLeft width="16px" height="16px" />}
          classes={styles.backButton}
        ></Button>
        <div className={styles.pageTitle}>
          <StoreIllustrated {...getIconDimensions()} className={styles.storeIcon({ variant })} />
          <div className={styles.pageTitleStoreName({ variant })}>JB Hi-Fi {storeData.pageTitle}</div>
        </div>
        {storeData?.messageType && storeData?.message ? (
          <Alert type={storeData.messageType} content={storeData.message} showIcon classes={styles.headerAlert} />
        ) : null}
      </div>
      <Grid container gutter="none" className={styles.pageBody}>
        <Grid item xs={12} lg={isPopOut ? 12 : 4} className={styles.pageColumn({ variant })}>
          <div className={styles.storeDetails}>
            <div className={styles.detailsAddressPhone}>
              <p className={styles.detailsAddress}>
                {storeAddressParts?.map(
                  (part, i) =>
                    part && (
                      <span key={i}>
                        {part}
                        <br />
                      </span>
                    )
                )}
              </p>
            </div>

            <div className={styles.detailsHoursRange}>
              <table className={styles.detailsHoursTable}>
                <tbody>{getOpeningHours()}</tbody>
              </table>
            </div>

            <p>
              <LinkButton label="Get directions" icon={<LocationIcon />} isLink address={storeAddressParts} />
            </p>

            <div className={`${styles.detailsAddressPhone} ${styles.detailsPhone({ variant })}`}>
              {storeData.storePhone &&
              storeData?.storeLocationType &&
              ['Store', 'Home'].includes(storeData?.storeLocationType) ? (
                <p>
                  <LinkButton label={storeData.storePhone} icon={<Phone />} address={storeAddressParts} />
                </p>
              ) : null}

              {jbSalesLine && storeData?.showSalesLineCallout ? (
                <p>
                  <LinkButton label={jbSalesLine} icon={<Phone />} address={storeAddressParts} />
                </p>
              ) : null}
            </div>

            {!isDesktop && !isPopOut ? (
              <StoreMap
                id="store-map-mobile"
                lat={storeData?._geoloc?.lat}
                lng={storeData?._geoloc?.lng}
                location={`JB Hi-Fi ${storeAddressParts.join(' ')}`}
                type={mapType}
                storeId={shopId!}
                className={styles.storeMap({ type: mapType })}
              />
            ) : null}

            {!isPopOut ? (
              storeData?.productRange ? (
                <div className={styles.detailsHoursRange}>
                  <h2>Product range</h2>
                  <ul>
                    {storeData.productRange.map((product: any) => (
                      <li key={product} className={styles.detailsRangeItem}>
                        {product}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null
            ) : null}
          </div>
        </Grid>
        {!isPopOut ? (
          <Grid item xs={12} lg={isPopOut ? 12 : 8} className={styles.pageColumn({ variant })}>
            {isDesktop ? (
              <StoreMap
                id="store-map"
                type={mapType}
                lat={storeData?._geoloc?.lat}
                lng={storeData?._geoloc?.lng}
                storeId={shopId!}
                className={styles.storeMap({ type: mapType })}
              />
            ) : null}

            {jobVacanciesUrl ? (
              <div id="store-links">
                <h3>Interested in working at JB?</h3>
                <p>
                  <Link href={jobVacanciesUrl} data-testid="job-vacancies">
                    View job vacancies
                  </Link>
                </p>
              </div>
            ) : null}
          </Grid>
        ) : null}
      </Grid>
    </div>
  );
};

export default StoreLayout;
